import {
  ChangeEvent,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import BetAutoSwitch from "./BetAutoSwitch";
import {
  betAutoStateType,
  betPlaceStatusType,
  cashingStatusType,
  dimensionType,
} from "../../@types";
import PIXIComponent from "../pixicomp";
import { useAviator } from "../../store/aviator";
import {
  Game_Global_Vars,
  getHistoryItemColor,
  playSound,
  setStateTemplate,
} from "../../utils";
import SnackBar from "../SnackBar";
import SwitchButton from "../SwitchButton";
import AutoBetModal from "../AutoBetModal";
import BetBoard from "./BetBoard";
import CustomSnackBar from "../CustomSnackBar";
import { MoreHorizontal, SquareMinus, CopyPlus } from "lucide-react";
import { GameStages, useSocket } from "../../providers/SocketProvider";
import ProvablyFairDetails from "../modals/provablyHistoryModal";
import ProvablyFairModal from "../modals/provablyFairSettingsModal";
import { loadRoundResults } from "../../services/roundResultsService";
const dummyUsers = [
  "/avatars/av-1.png",
  "/avatars/av-2.png",
  "/avatars/av-3.png",
];

type GameBoardProps = {
  bet6: string[];
  marketId?: string | null;
  onCashoutSuccess?: (data: any) => void;
};

const GameBoard = ({ bet6, marketId, onCashoutSuccess }: GameBoardProps) => {
  const [showProvablyFair, setShowProvablyFair] = useState(false);
  const [showProvablyFairSettings, setShowProvablyFairSettings] =
    useState(false);

  const [selectedRound, setSelectedRound] = useState<any>(null);
  const [roundsData, setRoundsData] = useState<any[]>([]);

  // Round history click handler
  const handleMultiplierClick = (item: string, historyIndex: number) => {
    // "1.46x" se "1.46" banado
    const clickedMultiplier = item.replace("x", "");

    // Prefer the same history position so duplicate multipliers open the
    // correct round, then fall back to a numeric multiplier match.
    const roundAtIndex = roundsData[historyIndex];
    const foundRound =
      Number(roundAtIndex?.multiplier) === Number(clickedMultiplier)
        ? roundAtIndex
        : roundsData.find(
            (round: any) =>
              Number(round?.multiplier) === Number(clickedMultiplier),
          );

    if (foundRound) {
      const fairness = foundRound.provablyFair || {};
      setSelectedRound({
        roundId: String(foundRound.roundId || "N/A"),
        multiplier: String(foundRound.multiplier || clickedMultiplier),
        createdAt: foundRound.createdAt || new Date().toISOString(),
        provablyFair: {
          clientSeed: Array.isArray(fairness.clientSeed)
            ? fairness.clientSeed
            : [],
          serverSeedHash: String(fairness.serverSeedHash || "N/A"),
          serverSeed: String(fairness.serverSeed || "N/A"),
          crashPoint: String(
            fairness.crashPoint || `${foundRound.multiplier || clickedMultiplier}x`,
          ),
          proof: String(fairness.proof || "N/A"),
        },
      });
      setShowProvablyFair(true);
    } else {
      // Fallback: Show modal with basic data
      setSelectedRound({
        roundId: "N/A",
        multiplier: clickedMultiplier,
        createdAt: new Date().toISOString(),
        provablyFair: {
          clientSeed: [],
          serverSeedHash: "N/A",
          serverSeed: "N/A",
          crashPoint: clickedMultiplier + "x",
          proof: "N/A",
        },
      });
      setShowProvablyFair(true);
    }
  };

  const { aviatorState, setAviatorState } = useAviator();
  const {
    userCount,
    sendMessage,
    isConnected,
    subscribe,
    gameData,
    roundId,
    currencyConfig,
    balance,
    isDemoMode,
  } = useSocket();

  const [autoPlayingIndex] = useState(0);
  const [, setLoadingCrashHistory] = useState(false);
  const [, setErrorLoadingHistory] = useState("");
  const [, setBetRound] = useState<(string | null)[]>([null, null]);
  const [betActive, setBetActive] = useState<boolean[]>([false, false]);
  const [snackState, setSnackState] = useState({
    open: false,
    msg: "",
  });
  const [modalAutoPlayOpen, modalAutoPlaySetOpen] = useState(false);
  const [, setRotate] = useState(0);
  const [trigParachute] = useState({ uniqId: 0, isMe: true });
  const [crashHistory, setCrashHistory] = useState<string[]>([]);
  const [crashColor, setCrashColor] = useState<string[]>([]);
  const [pixiDimension, setPixiDimension] = useState<dimensionType>({
    width: 0,
    height: 400,
  });
  const [isMobileCanvas, setIsMobileCanvas] = useState(window.innerWidth < 600);
  const [isDesktopLayout, setIsDesktopLayout] = useState(
    window.innerWidth >= 1024,
  );
  const [dummyPlayerCount, setDummyPlayerCount] = useState(130);
  const [displayedViewerCount, setDisplayedViewerCount] = useState(130);
  const viewerCountTargetRef = useRef(130);
  const pixi_ref = useRef<HTMLDivElement>(null);
  const footer_ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    viewerCountTargetRef.current = Math.max(100, userCount + dummyPlayerCount);
  }, [dummyPlayerCount, userCount]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setDisplayedViewerCount((previous) => {
        const difference = viewerCountTargetRef.current - previous;
        if (difference === 0) return previous;

        const step = Math.min(Math.abs(difference), Math.random() < 0.8 ? 1 : 2);
        return previous + Math.sign(difference) * step;
      });
    }, 1200);

    return () => window.clearInterval(interval);
  }, []);

  const [betButtonCount, setBetButtonCount] = useState(2);
  const [curPayout, setCurPayout] = useState(0);

  const [pendingBet, setPendingBet] = useState<boolean[]>([false, false]);
  const [betId, setBetId] = useState<(string | null)[]>([null, null]);
  const [betAutoState, setBetAutoState] = useState<betAutoStateType[]>([
    "bet",
    "bet",
  ]);
  const [betValue, setBetValue] = useState<string[]>([
    currencyConfig.minBet.toString(),
    currencyConfig.minBet.toString(),
  ]);
  const [cashedWin, setCashedWin] = useState<number[]>([0, 0]);
  const [enabledAutoCashOut, setEnabledAutoCashOut] = useState<boolean[]>([
    false,
    false,
  ]);
  const [autoCashVal, setAutoCashVal] = useState<string[]>(["1.45", "1.45"]);
  const [betPlaceStatus, setBetPlaceStatus] = useState<betPlaceStatusType[]>([
    "none",
    "none",
  ]);
  const [cashingStatus, setCashingStatus] = useState<cashingStatusType[]>([
    "none",
    "none",
  ]);

  const betActiveRef = useRef<boolean[]>([false, false]);
  const betPlaceStatusRef = useRef<betPlaceStatusType[]>(["none", "none"]);
  const pendingBetRef = useRef<boolean[]>([false, false]);
  const betValueRef = useRef<string[]>([
    currencyConfig.minBet.toString(),
    currencyConfig.minBet.toString(),
  ]);
  const enabledAutoCashOutRef = useRef<boolean[]>([false, false]);
  const autoCashValRef = useRef<string[]>(["1.45", "1.45"]);
  const remainingAutoPlayRef = useRef<number[]>([0, 0]);
  const sendMessageRef = useRef(sendMessage);
  const roundIdRef = useRef(roundId);
  const handleCashOutRef = useRef<(i: number, isAuto: boolean) => void>(
    () => {},
  );
  const handleBetRef = useRef<(i: number) => void>(() => {});
  const progressRef = useRef(0);
  const [cashes, setCashes] = useState<{ payout: number; win: number }[]>([]);

  const bgMusicRef = useRef<HTMLAudioElement | null>(null);
  const crashAudioRef = useRef<HTMLAudioElement | null>(null);
  const [needsBgUnlock, setNeedsBgUnlock] = useState(false);

  const [progress, setProgress] = useState(0);

  const cancelAutoPlay = useCallback(
    (i: number) => {
      setAviatorState((prev) => {
        const remaining = [...prev.RemainedAutoPlayCount];
        remaining[i] = 0;
        return { ...prev, RemainedAutoPlayCount: remaining };
      });
    },
    [setAviatorState],
  );

  useEffect(() => {
    betActiveRef.current = betActive;
  }, [betActive]);

  useEffect(() => {
    betPlaceStatusRef.current = betPlaceStatus;
  }, [betPlaceStatus]);

  useEffect(() => {
    pendingBetRef.current = pendingBet;
  }, [pendingBet]);

  useEffect(() => {
    betValueRef.current = betValue;
  }, [betValue]);

  useEffect(() => {
    setBetValue((previous) => {
      const next = previous.map((value) => {
        const parsed = Number.parseFloat(value);
        const normalized = Number.isFinite(parsed)
          ? parsed
          : currencyConfig.minBet;
        return Math.max(
          currencyConfig.minBet,
          Math.min(currencyConfig.maxBet, normalized),
        ).toString();
      });

      Game_Global_Vars.betValue = next;
      betValueRef.current = next;
      return next;
    });
  }, [currencyConfig.maxBet, currencyConfig.minBet]);

  useEffect(() => {
    enabledAutoCashOutRef.current = enabledAutoCashOut;
  }, [enabledAutoCashOut]);

  useEffect(() => {
    autoCashValRef.current = autoCashVal;
  }, [autoCashVal]);

  useEffect(() => {
    remainingAutoPlayRef.current = aviatorState.RemainedAutoPlayCount;
  }, [aviatorState.RemainedAutoPlayCount]);

  useEffect(() => {
    sendMessageRef.current = sendMessage;
  }, [sendMessage]);

  useEffect(() => {
    roundIdRef.current = roundId;
  }, [roundId]);

  useEffect(() => {
    progressRef.current = progress;
  }, [progress]);
  const progressInterval = useRef<NodeJS.Timeout | null>(null);

  // Track pending bets that need to be sent to server
  const pendingBetToSendRef = useRef<boolean[]>([false, false]);
  // Track if bet is in placing state (waiting for server response)
  const placingBetRef = useRef<boolean[]>([false, false]);

  const fetchCrashHistory = useCallback(async () => {
    try {
      setLoadingCrashHistory(true);
      setErrorLoadingHistory("");
      const validRounds = await loadRoundResults();
      setRoundsData(validRounds);
      setCrashHistory(validRounds.map((round) => `${round.multiplier}x`));
      setErrorLoadingHistory("");
    } catch {
      setErrorLoadingHistory("Failed to load history");
    } finally {
      setLoadingCrashHistory(false);
    }
  }, []);

  useEffect(() => {
    const handleSocketMessage = (data: any) => {
      if (data.type === "ERROR" && data.status === "error") {
        const index = Math.max(0, Number(data.betType || 1) - 1);
        _setPendingBet(false, index);
        _setBetPlaceStatus("none", index);
        _setBetId("", index);
        pendingBetToSendRef.current[index] = false;
        placingBetRef.current[index] = false;
        Game_Global_Vars.pendingBet[index] = false;
        cancelAutoPlay(index);
        setSnackState({
          open: true,
          msg: data.message || "Demo bet could not be placed.",
        });
        return;
      }

      /* ---------------- BET CONFIRMATION ---------------- */
      if (data.type === "3" && data.status === "success" && data.bets) {
        data.bets.forEach((bet: any) => {
          const i = Number(bet.betType) - 1;

          if (!bet.betId) return;

          // Update bet ID
          _setBetId(bet.betId, i);

          // Set bet status to success
          _setBetPlaceStatus("success", i);

          // Clear pending flags
          _setPendingBet(false, i);
          pendingBetToSendRef.current[i] = false;
          placingBetRef.current[i] = false;
          Game_Global_Vars.pendingBet[i] = false;

          // Update round tracking
          setBetRound((prev) => {
            const copy = [...prev];
            copy[i] = bet.round;
            return copy;
          });

          // Mark bet as active (eligible for cashout)
          setBetActive((prev) => {
            const copy = [...prev];
            copy[i] = true;
            return copy;
          });
        });
      }

      if (data.p === "CRASH" && data.cv) {
        // Refresh from the rounds API so the list and fairness details always
        // represent persisted server data.
        setTimeout(() => fetchCrashHistory(), 500);
      }

      /* ---------------- PLACEBETS UPDATE ---------------- */
      if (data.placeBets && Array.isArray(data.placeBets)) {
        data.placeBets.forEach((bet: any) => {
          const i = Number(bet.betType) - 1;

          // If we have betId, confirm the bet
          if (bet.betId && betPlaceStatusRef.current[i] !== "success") {
            _setBetId(bet.betId, i);
            _setBetPlaceStatus("success", i);
            _setPendingBet(false, i);
            setBetActive((prev) => {
              const copy = [...prev];
              copy[i] = true;
              return copy;
            });
          }
        });
      }

      /* ---------------- CASHOUT CONFIRM ---------------- */
      if (data.type === "cashout_success") {
        const i = Number(data.betType) - 1;

        // Deactivate bet after cashout
        setBetActive((prev) => {
          const copy = [...prev];
          copy[i] = false;
          return copy;
        });

        _setBetPlaceStatus("none", i);
        _setBetId("", i);
      }
    };

    const unsubscribe = subscribe("bet_result", handleSocketMessage);
    return unsubscribe;
  }, [cancelAutoPlay, subscribe, fetchCrashHistory]);

  useEffect(() => {
    const status = gameData.status;

    /* ---------------- WAIT ---------------- */
    if (status === GameStages.WAIT) {
      setCurPayout(1);
      Game_Global_Vars.allowedBet = true;

      // Collect all pending bets and send them in a single array
      const pendingBetsToSend: {
        action: string;
        round: string | number;
        stake: number;
        betType: string;
      }[] = [];
      const pendingIndices: number[] = [];
      let queuedStake = 0;

      [0, 1].forEach((i) => {
        if (pendingBetRef.current[i] && pendingBetToSendRef.current[i]) {
          const enteredStake = Number.parseFloat(betValueRef.current[i]);
          const stake = Math.max(
            currencyConfig.minBet,
            Math.min(
              currencyConfig.maxBet,
              Number.isFinite(enteredStake)
                ? enteredStake
                : currencyConfig.minBet,
            ),
          );

          if (isDemoMode && stake > balance - queuedStake) {
            _setPendingBet(false, i);
            _setBetPlaceStatus("none", i);
            _setBetId("", i);
            pendingBetToSendRef.current[i] = false;
            placingBetRef.current[i] = false;
            Game_Global_Vars.pendingBet[i] = false;
            cancelAutoPlay(i);
            setSnackState({
              open: true,
              msg: `Insufficient demo balance. Available: ${balance.toFixed(
                currencyConfig.decimals,
              )} ${currencyConfig.code}.`,
            });
            return;
          }

          if (stake !== enteredStake) {
            _setBetValue(stake.toString(), i);
          }

          pendingBetsToSend.push({
            action: "PlaceBet",
            round: roundIdRef.current,
            stake,
            betType: (i + 1).toString(),
          });
          pendingIndices.push(i);
          queuedStake += stake;

          // ✅ CRITICAL: Clear ref IMMEDIATELY to prevent duplicate sends
          pendingBetToSendRef.current[i] = false;
          placingBetRef.current[i] = false;
        }
      });

      // Send all pending bets in single message if any exist
      if (pendingBetsToSend.length > 0) {
        sendMessageRef.current(pendingBetsToSend);

        // Update state for all sent bets
        pendingIndices.forEach((i) => {
          const tempBetId = `temp-bet-${Date.now()}-${i}`;
          _setBetId(tempBetId, i);
          _setBetPlaceStatus("placing", i);
          _setPendingBet(false, i);
          Game_Global_Vars.pendingBet[i] = false;
          Game_Global_Vars.cashStarted[i] = false;
          _setCashingStatus("none", i);
        });
      }
    }

    /* ---------------- RUN ---------------- */
    if (status === GameStages.RUN) {
      Game_Global_Vars.allowedBet = false;

      // 🔥 CRITICAL FIX: If bet is "placing" and game is RUNNING,
      // automatically mark it as "success" (optimistic update)
      [0, 1].forEach((i) => {
        if (
          betPlaceStatusRef.current[i] === "placing" &&
          !betActiveRef.current[i]
        ) {
          setBetActive((prev) => {
            const copy = [...prev];
            copy[i] = true;
            return copy;
          });
        }
      });
    }

    /* ---------------- BLAST ---------------- */
    if (status === GameStages.BLAST) {
      Game_Global_Vars.allowedBet = false;

      // Only reset ACTIVE bets (bets that were placed and confirmed in current round)
      // Keep pending bets that need to be sent in next WAIT phase
      [0, 1].forEach((i) => {
        // If bet was active (confirmed by server in this round), reset it
        if (
          betActiveRef.current[i] ||
          betPlaceStatusRef.current[i] === "success"
        ) {
          _setBetPlaceStatus("none", i);
          _setBetId("", i);
          Game_Global_Vars.cashStarted[i] = false;
        }
        // If bet is pending (waiting for next round), KEEP it
        else if (pendingBetRef.current[i] && pendingBetToSendRef.current[i]) {
        }
        // If bet was "placing" but not confirmed, reset it
        else if (betPlaceStatusRef.current[i] === "placing") {
          _setPendingBet(false, i);
          _setBetPlaceStatus("none", i);
          _setBetId("", i);
          pendingBetToSendRef.current[i] = false;
          placingBetRef.current[i] = false;
          Game_Global_Vars.pendingBet[i] = false;
        }
      });

      // Reset active bets and cashing status
      setBetActive([false, false]);
      setBetRound([null, null]);
      setCashingStatus(["none", "none"]);
    }
  }, [
    balance,
    currencyConfig.maxBet,
    currencyConfig.minBet,
    currencyConfig.code,
    currencyConfig.decimals,
    cancelAutoPlay,
    gameData.status,
    isDemoMode,
  ]);

  const _setBetValue = (
    val: string | ((prev: string) => string),
    i: number,
  ) => {
    if (typeof val === "string") {
      Game_Global_Vars.betValue[i] = val;
      setBetValue((prev) => {
        const new_val = [...prev];
        new_val[i] = val;
        return new_val;
      });
    } else {
      setBetValue((prev) => {
        const new_val = [...prev];
        new_val[i] = val(prev[i]);
        Game_Global_Vars.betValue = new_val;
        return new_val;
      });
    }
  };

  const _setBetPlaceStatus = (
    val: betPlaceStatusType | betPlaceStatusType[],
    i: number,
  ) => {
    if (typeof val === "object") {
      Game_Global_Vars.betPlaceStatus = val;
      setBetPlaceStatus(val);
    } else {
      Game_Global_Vars.betPlaceStatus[i] = val;
      setBetPlaceStatus(setStateTemplate(val, i));
    }
  };

  const _setCashingStatus = (val: cashingStatusType, i: number) => {
    Game_Global_Vars.cashingStatus[i] = val;
    setCashingStatus(setStateTemplate(val, i));
  };

  const _setPendingBet = (val: boolean | boolean[], i: number) => {
    if (typeof val === "boolean") {
      setPendingBet(setStateTemplate(val, i));
    } else {
      setPendingBet(val);
    }
  };

  const _setBetId = (val: string | string[], i: number) => {
    if (typeof val === "string") {
      setBetId(setStateTemplate(val, i));
    } else {
      setBetId(val);
    }
  };

  const _setEnabledAutoCashOut = (val: boolean, i: number) => {
    setEnabledAutoCashOut(setStateTemplate(val, i));
  };

  const _setAutoCashVal = (val: string, i: number) => {
    setAutoCashVal(setStateTemplate(val, i));
  };

  const _setCashedWin = (val: number, i: number) => {
    setCashedWin(setStateTemplate(val, i));
  };

  const handleBetValueChange = (
    e: ChangeEvent<HTMLInputElement>,
    i: number,
  ) => {
    if (e.target.value.endsWith(".")) {
      _setBetValue(e.target.value, i);
    } else {
      const minBet = currencyConfig.minBet;
      const maxBet = currencyConfig.maxBet;
      const parsedValue = Number.parseFloat(e.target.value);
      let newValue = Number.isFinite(parsedValue)
        ? Math.round(parsedValue * 100) / 100
        : minBet;
      newValue = Math.max(minBet, Math.min(maxBet, newValue));
      _setBetValue(newValue.toString(), i);
    }
  };

  // Modify bet value with +/- buttons, respecting min/max limits
  const modifyBetValue = (amount: number, i: number) => {
    _setBetValue((prev) => {
      const currentVal = parseFloat(prev) || 0;
      const newVal = currentVal + amount;
      // Enforce min and max bet limits from currency config
      const clampedVal = Math.max(
        currencyConfig.minBet,
        Math.min(currencyConfig.maxBet, newVal),
      );
      return clampedVal.toString();
    }, i);
  };

  // Handle stake button click - if current bet < stake, set to stake; else add stake to bet
  const handleStakeButtonClick = (stakeAmount: number, i: number) => {
    _setBetValue((prev) => {
      const currentVal = parseFloat(prev) || 0;
      let newVal: number;

      if (currentVal < stakeAmount) {
        // Current bet is less than stake button value, set to stake value
        newVal = stakeAmount;
      } else {
        // Current bet is higher or equal, add stake value to current bet
        newVal = currentVal + stakeAmount;
      }

      // Enforce max bet limit
      newVal = Math.max(
        currencyConfig.minBet,
        Math.min(newVal, currencyConfig.maxBet),
      );

      return newVal.toString();
    }, i);
  };

  // Function to actually send bet to server (only during WAIT phase)
  const sendPendingBetToServer = async (i: number) => {
    if (!isConnected) {
      _setPendingBet(false, i);
      pendingBetToSendRef.current[i] = false;
      placingBetRef.current[i] = false;
      return;
    }

    if (!roundId) {
      _setPendingBet(false, i);
      pendingBetToSendRef.current[i] = false;
      placingBetRef.current[i] = false;
      return;
    }

    const enteredStake = Number.parseFloat(betValue[i]);
    const stake = Math.max(
      currencyConfig.minBet,
      Math.min(
        currencyConfig.maxBet,
        Number.isFinite(enteredStake) ? enteredStake : currencyConfig.minBet,
      ),
    );

    if (isDemoMode && stake > balance) {
      _setPendingBet(false, i);
      _setBetPlaceStatus("none", i);
      _setBetId("", i);
      pendingBetToSendRef.current[i] = false;
      placingBetRef.current[i] = false;
      Game_Global_Vars.pendingBet[i] = false;
      cancelAutoPlay(i);
      setSnackState({
        open: true,
        msg: `Insufficient demo balance. Available: ${balance.toFixed(
          currencyConfig.decimals,
        )} ${currencyConfig.code}.`,
      });
      return;
    }

    if (stake !== enteredStake) {
      _setBetValue(stake.toString(), i);
      setSnackState({
        open: true,
        msg: `Bet must be between ${currencyConfig.minBet} and ${currencyConfig.maxBet} ${currencyConfig.code}.`,
      });
    }

    // ✅ CRITICAL FIX: Set all pending flags BEFORE sending
    _setPendingBet(true, i);
    pendingBetToSendRef.current[i] = true;
    placingBetRef.current[i] = true;
    Game_Global_Vars.pendingBet[i] = true;

    // Set status to placing
    _setBetPlaceStatus("placing", i);
    Game_Global_Vars.cashStarted[i] = false;
    _setCashingStatus("none", i);

    try {
      const betMessage = [
        {
          action: "PlaceBet",
          round: roundId,
          stake,
          betType: (i + 1).toString(),
        },
      ];

      // This bet has now been handed to the local engine. Clearing the send
      // flag synchronously prevents the balance update from making the WAIT
      // effect submit the same bet a second time before React commits state.
      pendingBetToSendRef.current[i] = false;
      placingBetRef.current[i] = false;
      sendMessage(betMessage);

      // Create temporary bet ID
      const tempBetId = `temp-bet-${Date.now()}-${i}`;
      _setBetId(tempBetId, i);
    } catch {
      _setBetPlaceStatus("none", i);
      _setPendingBet(false, i);
      pendingBetToSendRef.current[i] = false;
      placingBetRef.current[i] = false;
    }
  };

  const handleBet = async (i: number) => {
    // Check if bet is already placed successfully
    if (betPlaceStatus[i] === "success") {
      return;
    }

    // Check if bet is already pending
    if (pendingBet[i]) {
      return;
    }

    // If game is in WAIT phase, send bet immediately to server
    if (gameData.status === GameStages.WAIT) {
      await sendPendingBetToServer(i);
      return;
    }

    // If game is in RUN phase, mark as pending but don't send to server
    if (gameData.status === GameStages.RUN) {
      // ✅ Set pending flags
      _setPendingBet(true, i);
      pendingBetToSendRef.current[i] = true;
      placingBetRef.current[i] = true;
      return;
    }

    // If game is in BLAST phase, mark as pending for next round
    if (gameData.status === GameStages.BLAST) {
      // ✅ Set pending flags
      _setPendingBet(true, i);
      pendingBetToSendRef.current[i] = true;
      placingBetRef.current[i] = true;
      return;
    }
  };
  handleBetRef.current = handleBet;

  const cancelBet = async (i: number) => {
    // Cancel pending bet (not yet sent to server)
    if (pendingBet[i] && !betId[i]) {
      _setPendingBet(false, i);
      pendingBetToSendRef.current[i] = false;
      placingBetRef.current[i] = false;
      Game_Global_Vars.pendingBet[i] = false;

      if (betPlaceStatus[i] === "placing") {
        _setBetPlaceStatus("none", i);
        _setBetId("", i);
      }
    }
    // Cancel confirmed bet (has betId, send cancel to socket)
    else if (betPlaceStatus[i] === "success" && betId[i]) {
      // Send cancel message to socket
      const cancelMessage = [
        {
          action: "CancelBet",
          betId: betId[i],
          betType: (i + 1).toString(),
          round: roundId,
        },
      ];

      sendMessage(cancelMessage);

      // Reset bet state
      _setPendingBet(false, i);
      _setBetPlaceStatus("none", i);
      _setBetId("", i);
      setBetActive((prev) => {
        const copy = [...prev];
        copy[i] = false;
        return copy;
      });
      pendingBetToSendRef.current[i] = false;
      placingBetRef.current[i] = false;
      Game_Global_Vars.pendingBet[i] = false;
    }

    if (aviatorState.RemainedAutoPlayCount[i] > 0) {
      cancelAutoPlay(i);
    }
  };

  const handleCashOut = async (i: number, auto?: boolean) => {
    if (Game_Global_Vars.cashStarted[i]) return;

    // Check if we have an active bet
    if (!betActive[i] || !betId[i] || betId[i] === null) {
      return;
    }

    // Check if game is running
    if (gameData.status !== GameStages.RUN) {
      return;
    }

    Game_Global_Vars.cashStarted[i] = true;
    _setCashingStatus("caching", i);

    try {
      const cashoutMessage = {
        action: "CashoutBet",
        betType: (i + 1).toString(),
        round: roundId,
        RUNValue: curPayout,
        betid: betId[i],
      };
      sendMessage(cashoutMessage);

      // Calculate win amount
      const winAmount = curPayout * parseFloat(betValue[i]);

      // Update UI optimistically
      _setCashedWin(winAmount, i);
      _setCashingStatus("success", i);

      // Deactivate bet
      setBetActive((prev) => {
        const copy = [...prev];
        copy[i] = false;
        return copy;
      });

      _setBetPlaceStatus("none", i);
      _setBetId("", i);

      // Update balance
      setAviatorState((prev) => ({
        ...prev,
        balance: prev.balance + winAmount,
      }));

      if (onCashoutSuccess) {
        const cashoutData = {
          data: [
            {
              betId: betId[i],
              stake: parseFloat(betValue[i]),
              cashout: winAmount,
              cashOutAtMultiplier: curPayout.toFixed(2),
            },
          ],
          meta: { status: true, message: "Cashout successful" },
        };
        onCashoutSuccess(cashoutData);
      }
    } catch {
      _setCashingStatus("none", i);
      Game_Global_Vars.cashStarted[i] = false;
    }
  };
  handleCashOutRef.current = handleCashOut;

  useEffect(() => {
    setCrashColor(crashHistory.map((item) => getHistoryItemColor(item)));
  }, [crashHistory]);

  useEffect(() => {
    if (gameData.status === GameStages.WAIT && !progressInterval.current) {
      setCurPayout(1.0);
      const totalDuration = 9000;
      const startsIn = (gameData.startsIn || 0) * 1100;
      const intervalMs = 20;
      Game_Global_Vars.allowedBet = true;

      if (progressRef.current <= 0) {
        setProgress(100 / (totalDuration / startsIn));
      }

      progressInterval.current = setInterval(() => {
        const decrement = 100 / (totalDuration / intervalMs);
        setProgress((prev) => prev - decrement);
      }, intervalMs);
    }

    if (gameData.status === GameStages.RUN) {
      setProgress(0);
      Game_Global_Vars.allowedBet = false;

      if (progressInterval.current) {
        clearInterval(progressInterval.current);
        progressInterval.current = null;
      }
    }

    if (gameData.status === GameStages.BLAST) {
      Game_Global_Vars.allowedBet = false;

      if (progressInterval.current) {
        clearInterval(progressInterval.current);
        progressInterval.current = null;
      }
    }
  }, [gameData.status, gameData.startsIn]);

  useEffect(() => {
    if (gameData.multiplier) {
      setCurPayout(gameData.multiplier);
      Game_Global_Vars.curPayout = gameData.multiplier;
    }
  }, [gameData.multiplier]);

  // Auto Cashout: Trigger cashout when multiplier reaches target value
  useEffect(() => {
    const currentMultiplier = gameData.multiplier;
    if (gameData.status === GameStages.RUN && currentMultiplier != null) {
      [0, 1].forEach((i) => {
        // Check if auto cashout is enabled and bet is active
        if (
          enabledAutoCashOutRef.current[i] &&
          betActiveRef.current[i] &&
          betPlaceStatusRef.current[i] === "success" &&
          !Game_Global_Vars.cashStarted[i]
        ) {
          const targetMultiplier = parseFloat(autoCashValRef.current[i]) || 1.5;

          // Trigger cashout when current multiplier reaches or exceeds target
          if (currentMultiplier >= targetMultiplier) {
            handleCashOutRef.current(i, true); // true = auto cashout
          }
        }
      });
    }
  }, [gameData.multiplier, gameData.status]);

  // Play sounds based on game status changes
  const prevStatusRef = useRef<GameStages | undefined>(undefined);
  useEffect(() => {
    // Only play sounds if status actually changed
    if (prevStatusRef.current === gameData.status) return;

    const prevStatus = prevStatusRef.current;
    prevStatusRef.current = gameData.status;

    // Play crash sound when BLAST happens
    if (gameData.status === GameStages.BLAST && aviatorState.fxChecked) {
      playSound("flew");
    }

    // Play take sound when RUN starts (plane takes off)
    if (
      gameData.status === GameStages.RUN &&
      prevStatus === GameStages.WAIT &&
      aviatorState.fxChecked
    ) {
      playSound("take");
    }
  }, [gameData.status, aviatorState.fxChecked]);

  const handleResize = useCallback(() => {
    const containerWidth = pixi_ref.current?.clientWidth || 0;
    const fallbackWidth =
      window.visualViewport?.width || window.innerWidth || 0;
    const effectiveWidth = containerWidth || fallbackWidth || window.innerWidth;

    const width = Math.min(aviatorState.dimension.width, effectiveWidth);

    const isMobile = window.innerWidth < 768;
    const isTablet = window.innerWidth >= 768 && window.innerWidth < 1024;
    const isDesktop = window.innerWidth >= 1024;
    setIsDesktopLayout(isDesktop);

    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    // For desktop: Calculate available height dynamically to fill space without scroll
    if (isDesktop) {
      // Get actual heights of elements
      const headerHeight = 50; // TopLogoBar fixed height
      const roundHistoryHeight = 36; // Crash history row
      const betPanelsHeight = footer_ref.current?.clientHeight || 184; // Bet buttons section (measure actual)

      // Calculate available height: viewport - header - history - bet panels - 2px gap
      let availableHeight =
        screenHeight - headerHeight - roundHistoryHeight - betPanelsHeight - 2;

      // Special handling for small height laptops (1024+ width with height <= 600px) - Nest Hub view
      const isSmallHeightLaptop = screenWidth >= 1024 && screenHeight <= 600;

      // For Nest Hub (1024x600): Force canvas height to 330px
      const canvasHeight = isSmallHeightLaptop
        ? 330
        : Math.max(250, availableHeight);

      setPixiDimension({ width, height: canvasHeight });

      setAviatorState((prev) => {
        const new_width = prev.dimension.width;
        const new_height = width
          ? (new_width * canvasHeight) / width
          : prev.dimension.height;
        return {
          ...prev,
          dimension: { width: new_width, height: new_height },
        };
      });
      return;
    }

    const footerHeight = footer_ref.current?.clientHeight || 0;

    let totalReservedHeight = footerHeight;

    if (isMobile) totalReservedHeight += 235;
    if (isTablet) totalReservedHeight += 400;

    let height = screenHeight - totalReservedHeight;

    let maxCanvasHeight = Infinity;

    if (isTablet) {
      maxCanvasHeight = 328;
    }

    if (screenWidth < 600) {
      setIsMobileCanvas(true);
      switch (true) {
        case screenWidth <= 320:
          height = 200;
          break;
        case screenWidth < 345:
          height = 386;
          break;
        case screenWidth < 360:
          height = 244;
          break;
        case screenWidth <= 375:
          height = 200;
          break;
        case screenWidth <= 390:
          height = 348;
          break;
        case screenWidth <= 412:
          height = 419;
          break;
        case screenWidth <= 415:
          height = 400;
          break;
        case screenWidth <= 430:
          height = 436;
          break;
        case screenWidth <= 440:
          height = 261;
          break;
        default:
          height = 224;
          break;
      }
      setPixiDimension({ width, height });
      setAviatorState((prev) => {
        const new_width = prev.dimension.width;
        const new_height = width
          ? (new_width * height) / width
          : prev.dimension.height;
        return {
          ...prev,
          dimension: { width: new_width, height: new_height },
        };
      });
      return;
    } else {
      setIsMobileCanvas(false);
      let minCanvasHeight = 200;
      switch (true) {
        case screenWidth <= 900:
          minCanvasHeight = 328;
          break;
        case screenWidth <= 1028 && screenWidth >= 1020 && screenHeight >= 1000:
          minCanvasHeight = 1100;
          break;
        default:
          minCanvasHeight = 200;
          break;
      }
      height = Math.max(Math.min(height, maxCanvasHeight), minCanvasHeight);
    }

    setPixiDimension({ width, height });

    setAviatorState((prev) => {
      const new_width = prev.dimension.width;
      const new_height = width
        ? (new_width * height) / width
        : prev.dimension.height;
      return {
        ...prev,
        dimension: { width: new_width, height: new_height },
      };
    });
  }, [aviatorState.dimension.width, setAviatorState]);

  const handleBetBoardSwitch = () => {
    setBetButtonCount((prev) => (prev === 1 ? 2 : 1));
  };

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    const rotateInterval = setInterval(
      () => setRotate((prev) => prev + 10),
      100,
    );

    return () => {
      window.removeEventListener("resize", handleResize);
      clearInterval(rotateInterval);
    };
  }, [handleResize]);

  useEffect(() => {
    if (aviatorState.musicChecked) {
      if (!bgMusicRef.current) {
        bgMusicRef.current = new Audio("/sounds/bg_music.mp3");
        bgMusicRef.current.loop = true;
        bgMusicRef.current.volume = (aviatorState.vol ?? 50) / 100;
        // Attempt autoplay; if blocked, we'll retry on user gesture.
        bgMusicRef.current
          .play()
          .then(() => setNeedsBgUnlock(false))
          .catch(() => setNeedsBgUnlock(true));
      } else {
        bgMusicRef.current.volume = (aviatorState.vol ?? 50) / 100;
        bgMusicRef.current
          .play()
          .then(() => setNeedsBgUnlock(false))
          .catch(() => setNeedsBgUnlock(true));
      }
    } else {
      if (bgMusicRef.current) {
        bgMusicRef.current.pause();
        bgMusicRef.current.currentTime = 0;
      }
      setNeedsBgUnlock(false);
    }
    return () => {
      if (bgMusicRef.current) {
        bgMusicRef.current.pause();
        bgMusicRef.current.currentTime = 0;
        bgMusicRef.current = null;
      }
    };
  }, [aviatorState.musicChecked, aviatorState.vol]);

  useEffect(() => {
    if (!needsBgUnlock) return;

    const handleUserGesture = () => {
      if (!aviatorState.musicChecked || !bgMusicRef.current) return;
      bgMusicRef.current
        .play()
        .then(() => setNeedsBgUnlock(false))
        .catch(() => setNeedsBgUnlock(true));
    };

    window.addEventListener("pointerdown", handleUserGesture);
    window.addEventListener("keydown", handleUserGesture);
    return () => {
      window.removeEventListener("pointerdown", handleUserGesture);
      window.removeEventListener("keydown", handleUserGesture);
    };
  }, [needsBgUnlock, aviatorState.musicChecked]);

  useEffect(() => {
    if (aviatorState.musicChecked) {
      if (bgMusicRef.current) {
        bgMusicRef.current.volume = (aviatorState.vol ?? 50) / 100;
      }
    }
  }, [aviatorState.musicChecked, aviatorState.vol]);

  useEffect(() => {
    if (!aviatorState.fxChecked && crashAudioRef.current) {
      crashAudioRef.current.pause();
      crashAudioRef.current.currentTime = 0;
      crashAudioRef.current = null;
    }
  }, [aviatorState.fxChecked]);

  useEffect(() => {
    if (gameData.status === GameStages.WAIT) {
      [0, 1].forEach((i) => {
        if (
          remainingAutoPlayRef.current[i] > 0 &&
          !pendingBetRef.current[i] &&
          betPlaceStatusRef.current[i] !== "placing"
        ) {
          handleBetRef.current(i);
        }
      });
    }
  }, [gameData.status]);

  useEffect(() => {
    fetchCrashHistory();
  }, [fetchCrashHistory]);

  const [showExpanded, setShowExpanded] = useState(false);

  return (
    <div
      className="flex overflow-hidden"
      style={{ height: "calc(100vh - 50px)" }}
    >
      <CustomSnackBar cashes={cashes} setCashes={setCashes} />
      <div
        className="w-full lg:w-[320px] xl:w-[420px] hidden lg:block h-full"
        style={{ marginTop: "0.2rem", paddingLeft: "2px" }}
      >
        <BetBoard
          onDummyPlayerCountChange={
            isDesktopLayout ? setDummyPlayerCount : undefined
          }
        />
      </div>
      <div
        className={`flex flex-col gap-1 lg:gap-0 w-full p-1 pt-0 text-white overflow-auto pb-0 lg:w-[calc(100%-320px)] xl:w-[calc(100%-410px)] ${betAutoState.includes("auto") ? "" : "lg:overflow-hidden"}`}
      >
        <div className="relative w-full">
          <div className="flex justify-between items-center rounded px-2 py-1">
            <div className="flex gap-x-2 flex-wrap overflow-hidden pr-4 py-1 max-h-[26px]">
              {crashHistory.slice(0, 50).map((item, i) => (
                <span
                  onClick={() => handleMultiplierClick(item, i)}
                  key={i}
                  style={{ color: crashColor[i], letterSpacing: "-.18px" }}
                  className={`block px-1 py-[2px] rounded-full text-[12px] font-[500] cursor-pointer`}
                >
                  {item}
                </span>
              ))}
            </div>

            <button
              className="p-1 rounded-full bg-[#2c2d30] hover:bg-[#3a3b3e] transition"
              style={{
                width: "25px",
                height: "16px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              onClick={() => setShowExpanded(true)}
            >
              <MoreHorizontal className="text-gray-400 w-4 h-4" />
            </button>
          </div>

          {showExpanded && (
            <div
              className="absolute z-50 right-0 top-1 w-full w-full bg-[#1e1f21] text-gray-200 rounded-lg shadow-lg px-2 py-1 pl-3"
              style={{ width: "calc(100% + 8px)", left: "-4px" }}
            >
              <div className="flex justify-between items-center mb-2">
                <span className=" text-gray-400" style={{ fontSize: "12px" }}>
                  Round History
                </span>
                <button
                  onClick={() => setShowExpanded(false)}
                  className="text-gray-400 font-bold hover:text-white text-sm"
                  style={{
                    fontSize: "12px",
                    borderRadius: "1rem",
                    width: "25px",
                    height: "16px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "#2c2d30",
                  }}
                >
                  ✕
                </button>
              </div>
              <div className="flex flex-wrap gap-[4px] text-[12px]">
                {crashHistory.map((item, i) => (
                  <span
                    onClick={() => handleMultiplierClick(item, i)}
                    key={i}
                    style={{ color: crashColor[i] }}
                    className="px-[2px] py-[4px] cursor-pointer cursor-pointer"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-col flex-1">
          <div
            className="text-center text-sm font-bold w-full rounded-t-[20px] border border-[#e59407] hidden"
            style={{
              background: "rgba(229, 148, 7, .8)",
            }}
          >
            FUN MODE
          </div>
          <div
            className={`flex justify-center w-full relative ${!isMobileCanvas ? "flex-1" : ""}`}
            style={{
              border: "1px solid #2a2b2e",
              borderBottomLeftRadius: "20px",
              borderBottomRightRadius: "20px",
              borderTopRightRadius: "20px",
              borderTopLeftRadius: "20px",
              height: `${pixiDimension.height}px`,
              maxHeight: `${pixiDimension.height}px`,
              minHeight: `${pixiDimension.height}px`,
            }}
            ref={pixi_ref}
          >
            <div className="absolute bottom-2 right-2 bg-[#2c2d30] p-1 pr-2 rounded-full">
              <div className="flex items-center gap-2 text-xs">
                <div className="flex -space-x-2">
                  {dummyUsers.map((u, i) => (
                    <img
                      key={i}
                      src={u}
                      alt="avatar"
                      width={24}
                      height={24}
                      className="rounded-full border border-[#28a90a] bg-[#232325]"
                      style={{ zIndex: 10 - i }}
                    />
                  ))}
                </div>
                <div>{displayedViewerCount}</div>
              </div>
            </div>
            <div
              className="flex flex-col gap-10 absolute top-0 left-0 right-0 justify-center items-center"
              style={{
                height: `${pixiDimension.height}px`,
                maxHeight: `${pixiDimension.height}px`,
                display: gameData.status !== GameStages.RUN ? "flex" : "none",
                gap: pixiDimension.height < 200 ? 2 : 40,
                zIndex: 10,
                pointerEvents: "none",
              }}
            >
              <div
                style={{
                  display:
                    gameData.status === GameStages.WAIT ? "block" : "none",
                }}
              >
                <div className="flex flex-col items-center justify-center w-[300px] rounded-lg">
                  <div className="flex flex-col items-center">
                    <div className="flex items-center gap-4">
                      <img
                        src="/aviator-logo.svg"
                        alt="Aviator"
                        className="h-10 logo-tint"
                      />
                      <div className="w-px h-10 bg-gray-300" />
                      <img
                        src="/aviator-plane.png"
                        alt="Aviator plane"
                        className="h-10 rotate-[340deg]"
                      />
                    </div>
                    <div className="mt-4 h-2 bg-gray-700 rounded w-[250px]">
                      <div
                        className={`h-2 bg-red-600 rounded ${
                          progress > 0 ? "transition-all duration-100" : ""
                        }`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-1 justify-center items-center px-4 py-2 lg:px-8 lg:py-2 rounded-lg">
                <p className="text-white uppercase text-[20px] lg:text-[30px]">
                  {gameData.status === GameStages.BLAST ? "Flew away!" : ""}
                </p>
                {gameData.status === GameStages.BLAST && (
                  <p className="text-[#d0011b] font-bold text-[56px] leading-[42px] lg:text-[100px] lg:leading-[100px]">
                    {curPayout.toFixed(2)}x
                  </p>
                )}
              </div>
            </div>
            <PIXIComponent
              pixiDimension={pixiDimension}
              curPayout={gameData?.multiplier || 1.0}
              trigParachute={trigParachute}
            />
          </div>
        </div>

        <div className="flex flex-col w-full lg:mt-[2px] mt-1" ref={footer_ref}>
          <div
            className={`grid grid-cols-1 gap-1 relative ${
              betButtonCount === 1 ? "" : "lg:grid-cols-2 md:grid-cols-2"
            }`}
          >
            {[0, 1].map((item, i) => (
              <div
                key={i}
                className={`relative flex flex-col justify-start items-center gap-1 lg:gap-2 w-full rounded-2xl bg-gradient-to-b from-[#1C1C1C] to-black p-3 pt-2 ${
                  betAutoState[i] === "auto"
                    ? "lg:h-auto lg:min-h-[225px] lg:pb-4"
                    : "lg:h-[184px]"
                } ${
                  i === 1 && betButtonCount === 1 ? "hidden" : ""
                }`}
                style={{ background: "#1b1c1d" }}
              >
                <BetAutoSwitch
                  disabled={aviatorState.RemainedAutoPlayCount[i] > 0}
                  betAuto={{
                    betAutoState: betAutoState[i],
                    setBetAutoState: (val: betAutoStateType) =>
                      setBetAutoState((prev) => {
                        const new_state = [...prev];
                        new_state[i] = val;
                        return new_state;
                      }),
                  }}
                />

                <div
                  className={`w-full xl:w-[390px] lg:w-full flex gap-2 items-center relative min-h-fit ${betAutoState[i] !== "auto" ? "lg:py-2" : ""}`}
                >
                  {(betButtonCount === 1 && item === 0) || item === 1 ? (
                    <button
                      onClick={handleBetBoardSwitch}
                      disabled={
                        betPlaceStatus[1] === "success" || pendingBet[1]
                      }
                      className={`absolute right-1 lg:-top-12 -top-[37px] rounded-full bg-[#2c2d30] w-[32px] h-[32px] flex justify-center items-center cursor-pointer disabled:opacity-30 hover:bg-[#2a2a2a] transition-colors border-4 border-[#141516]`}
                    >
                      {betButtonCount === 1 ? (
                        <CopyPlus className="w-4 h-4 text-green-700" />
                      ) : (
                        <SquareMinus className="w-4 h-4 text-gray-400" />
                      )}
                    </button>
                  ) : null}
                  <div className="flex flex-col w-[42%] lg:w-[165px] 3xl:w-[260px] h-full">
                    <div
                      className="flex justify-between items-center text-[10px] 3xl:text-xl w-full h-[27px] 3xl:h-[54px] bg-[#171717] rounded-full px-2"
                      style={{
                        fontFamily: "Roboto",
                        paddingBlock: "1rem",
                      }}
                    >
                      <button
                        disabled={
                          betPlaceStatus[i] === "success" || pendingBet[i]
                        }
                        onClick={() =>
                          modifyBetValue(-currencyConfig.stepAmount, i)
                        }
                        className="flex justify-center items-center w-6 h-6 3xl:w-9 3xl:h-9 text-gray-400 font-bold text-base 3xl:text-xl rounded-full bg-[#3e3e3e]"
                      >
                        −
                      </button>
                      <input
                        disabled={
                          betPlaceStatus[i] === "success" || pendingBet[i]
                        }
                        onChange={(e) => handleBetValueChange(e, i)}
                        value={betValue[i]}
                        type="string"
                        className="w-[30px] lg:w-[70px] 3xl:w-[150px] bg-transparent text-center text-[13px] 3xl:text-[26px]"
                        style={{
                          fontWeight: "600",
                          color: "#ccc",
                          fontSize: "1rem",
                        }}
                      />
                      <button
                        disabled={
                          betPlaceStatus[i] === "success" || pendingBet[i]
                        }
                        onClick={() =>
                          modifyBetValue(currencyConfig.stepAmount, i)
                        }
                        className="flex justify-center items-center w-6 h-6 3xl:w-9 3xl:h-9 text-gray-400 font-bold text-base 3xl:text-xl rounded-full bg-[#3e3e3e]"
                      >
                        +
                      </button>
                    </div>
                    <div
                      className="grid grid-cols-2 gap-1 w-full h-fit"
                      style={{ marginTop: "0.3rem" }}
                    >
                      {currencyConfig.stakes.map((stake, j) => (
                        <button
                          disabled={
                            betPlaceStatus[i] === "success" || pendingBet[i]
                          }
                          key={j}
                          onClick={() => handleStakeButtonClick(stake, i)}
                          className="flex justify-center font-bold items-center bg-[#171717] rounded-lg gap-[2px] w-full h-[25px] 3xl:h-9 text-[13px] 3xl:text-[26px] text-gray-500"
                        >
                          {stake}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* ✅ FIXED BUTTON LOGIC */}
                  {betPlaceStatus[i] === "success" ||
                  betPlaceStatus[i] === "placing" ||
                  pendingBet[i] ||
                  betActive[i] ? (
                    gameData.status === GameStages.RUN && betActive[i] ? (
                      // ✅ CASHOUT button when bet is SUCCESS and game is RUNNING
                      <button
                        onClick={() => handleCashOut(i, false)}
                        disabled={cashingStatus[i] === "caching"}
                        className="flex flex-col min-w-[58%] lg:min-w-[180px] h-[80px] 3xl:w-[395px] 3xl:h-[142px] rounded-[14px] 3xl:rounded-[30px] bg-[#d07206] justify-center items-center font-bold border border-[#ffbd71] hover:opacity-80 disabled:opacity-50 lg:w-[calc(100%-140px)]"
                      >
                        <h4 className="text-[16px] 3xl:text-[42px] leading-[20px] 3xl:leading-[42px] uppercase">
                          {cashingStatus[i] === "none"
                            ? "Cash Out"
                            : cashingStatus[i] === "caching"
                              ? "Cashing Out..."
                              : cashingStatus[i] === "success"
                                ? `Cashed ${cashedWin[i].toFixed(2)} ${currencyConfig.code}`
                                : "Failed"}
                        </h4>
                        {cashingStatus[i] === "none" && (
                          <h4 className="text-[20px]">
                            {(curPayout * parseFloat(betValue[i])).toFixed(2)}{" "}
                            {currencyConfig.code}
                          </h4>
                        )}
                      </button>
                    ) : gameData.status === GameStages.RUN && pendingBet[i] ? (
                      // ✅ CANCEL button when bet is PENDING during RUN phase (waiting for next round)
                      <button
                        onClick={() => cancelBet(i)}
                        className="flex flex-col min-w-[58%] lg:min-w-[180px] h-[80px] 3xl:w-[395px] 3xl:h-[142px] rounded-[14px] 3xl:rounded-[30px] bg-red-600 justify-center items-center border border-white hover:opacity-80 lg:w-[calc(100%-140px)]"
                      >
                        <p className="text-[22px] 3xl:text-[42px] leading-[20px] 3xl:leading-[42px]">
                          Cancel
                        </p>
                        <p className="text-[14px]">Waiting for next round</p>
                      </button>
                    ) : betPlaceStatus[i] === "placing" ? (
                      // ✅ CANCEL button while bet is placing (waiting for server)
                      <button
                        onClick={() => cancelBet(i)}
                        className="flex flex-col min-w-[58%] lg:min-w-[180px] h-[80px] 3xl:w-[395px] 3xl:h-[142px] rounded-[14px] 3xl:rounded-[30px] bg-red-600 justify-center items-center border border-white hover:opacity-80 lg:w-[calc(100%-140px)]"
                      >
                        <p className="text-[22px] 3xl:text-[42px] leading-[20px] 3xl:leading-[42px]">
                          Cancel
                        </p>
                        <p className="text-[14px]">Confirming bet</p>
                      </button>
                    ) : gameData.status === GameStages.WAIT ? (
                      // ✅ CANCEL button when bet is placed/pending during WAIT phase
                      <button
                        onClick={() => cancelBet(i)}
                        className="flex flex-col min-w-[58%] lg:min-w-[180px] h-[80px] 3xl:w-[395px] 3xl:h-[142px] rounded-[14px] 3xl:rounded-[30px] bg-red-600 justify-center items-center border border-white hover:opacity-80 lg:w-[calc(100%-140px)]"
                      >
                        <p className="text-[22px] 3xl:text-[42px] leading-[20px] 3xl:leading-[42px]">
                          Cancel
                        </p>
                      </button>
                    ) : (
                      // ✅ Bet placed but game is BLAST phase - show Cancel (red)
                      <button
                        onClick={() => cancelBet(i)}
                        className="flex flex-col min-w-[58%] lg:min-w-[180px] h-[80px] 3xl:w-[395px] 3xl:h-[142px] rounded-[14px] 3xl:rounded-[30px] bg-red-600 justify-center items-center border border-white hover:opacity-80 lg:w-[calc(100%-140px)]"
                      >
                        <p className="text-[22px] 3xl:text-[42px] leading-[20px] 3xl:leading-[42px]">
                          Cancel
                        </p>
                        <p className="text-[14px]">Waiting for next round</p>
                      </button>
                    )
                  ) : (
                    // ✅ BET button when no bet is placed - always green
                    <button
                      onClick={() => handleBet(i)}
                      className="flex flex-col min-w-[58%] lg:min-w-[180px] h-[80px] 3xl:w-[395px] 3xl:h-[142px] rounded-[14px] 3xl:rounded-[30px] bg-[#28a909] justify-center items-center lg:w-[calc(100%-140px)] border border-white hover:opacity-80"
                    >
                      <p className="text-[22px] 3xl:text-[42px] leading-[20px] 3xl:leading-[42px]">
                        Bet
                      </p>
                      <p className="text-[22px]">
                        {betValue[i]} {currencyConfig.code}
                      </p>
                    </button>
                  )}
                </div>
                {betAutoState[i] === "auto" && (
                  <div
                    className="flex justify-between gap-2 items-center mt-2 lg:mt-0"
                    style={{ width: "inherit", maxWidth: "350px" }}
                  >
                    <div className="flex gap-2 items-center">
                      <span className="text-[#939393] text-[12px] whitespace-nowrap">
                        Auto Play
                      </span>

                      <SwitchButton
                        disabled={
                          betPlaceStatus[i] === "success" || pendingBet[i]
                        }
                        checked={aviatorState.RemainedAutoPlayCount[i] > 0}
                        onChange={(_, checked) => {
                          setAviatorState((prev) => {
                            const copy = [...prev.RemainedAutoPlayCount];
                            copy[i] = checked ? 999999 : 0; // infinite auto play
                            return { ...prev, RemainedAutoPlayCount: copy };
                          });
                        }}
                      />
                    </div>

                    <div className="flex gap-1 items-center">
                      <span className="text-[#939393] text-[12px] whitespace-nowrap">
                        Auto Cash Out
                      </span>
                      <SwitchButton
                        disabled={
                          betPlaceStatus[i] === "success" || pendingBet[i]
                        }
                        checked={enabledAutoCashOut[i]}
                        onChange={(_, checked) => {
                          _setEnabledAutoCashOut(checked, i);
                          Game_Global_Vars.enabledAutoCashOut[i] = checked;
                        }}
                      />
                      <div className="flex gap-1 px-3 py-[2px] rounded-full bg-[#141516] text-[12px]">
                        <input
                          disabled={!enabledAutoCashOut}
                          readOnly={
                            betPlaceStatus[i] === "success" || pendingBet[i]
                          }
                          type="text"
                          value={autoCashVal[i]}
                          onChange={(e) => {
                            const val =
                              e.target.value.replace(/[^\d.]/g, "").trim() ||
                              "1";
                            _setAutoCashVal(val, i);
                            Game_Global_Vars.autoCashVal[i] = val;
                          }}
                          size={1}
                          className="bg-transparent w-12 outline-none disabled:text-[#888] text-center"
                        />
                        <span
                          className={`inline-block ${
                            enabledAutoCashOut ? "text-white" : "text-[#888]"
                          }`}
                        >
                          x
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <SnackBar snackState={snackState} setSnackState={setSnackState} />
          <AutoBetModal
            modalOpen={modalAutoPlayOpen}
            modalSetOpen={modalAutoPlaySetOpen}
            autoPlayingIndex={autoPlayingIndex}
          />
          <ProvablyFairDetails
            isOpen={showProvablyFair}
            onClose={() => setShowProvablyFair(false)}
            onOpenProvablyFairModal={() => {
              setShowProvablyFair(false);
              setShowProvablyFairSettings(true);
            }}
            roundData={
              selectedRound || {
                roundId: "000000",
                multiplier: "1.00",
                createdAt: new Date().toISOString(),
                provablyFair: {
                  clientSeed: [],
                  serverSeedHash: "",
                  serverSeed: "",
                  crashPoint: "1.00x",
                  proof: "",
                },
              }
            }
          />
          <ProvablyFairModal
            isOpen={showProvablyFairSettings}
            onClose={() => setShowProvablyFairSettings(false)}
          />
        </div>
        <div className="w-full lg:hidden h-full">
          <BetBoard
            onDummyPlayerCountChange={
              isDesktopLayout ? undefined : setDummyPlayerCount
            }
          />
        </div>
      </div>
    </div>
  );
};

export default GameBoard;
